/**
 * Equal-column grid of feature cards (icon placeholder + title + body).
 * Ported from ThemeMaker's {@code blocks/grid/FeatureGrid}.
 *
 * <p>Column count derives from the number of items so a 2-item grid
 * splits 50/50 and a 4-item one becomes 4 narrow cards. Themes wanting
 * a fixed-width auto-fit instead should override the block.
 */
'use client';

import { useBlockPart } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { tx, asList } from '../_shared/coerce';
import { cardStyle, muted } from '../_shared/styles';
import { SectionShell } from '../_shared/SectionShell';
import { type PaddingBox } from '../_shared/resolvePadding';

type Localized = string | Record<string, string>;

interface FeatureItem {
  title?: Localized;
  body?: Localized;
  icon?: string;
}

export interface FeatureGridProps {
  title?: Localized;
  items?: FeatureItem[];
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

export function FeatureGrid({ title, items, paddingOuter, paddingInner }: FeatureGridProps) {
  const part = useBlockPart();
  const list = asList<FeatureItem>(items);
  if (list.length === 0) return null;
  return (
    <SectionShell title={tx(title)} paddingOuter={paddingOuter} paddingInner={paddingInner}>
      <div
        {...part('list')}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${list.length}, 1fr)`,
          gap: 'var(--space-6, 24px)',
        }}
      >
        {list.map((it, i) => (
          <Card
            key={i}
            {...part('item', i)}
            style={{ ...cardStyle, padding: 'var(--space-6, 24px)' }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 'var(--radius-md, 8px)',
                background: 'var(--color-brand-50, #f0f4ff)',
                marginBottom: 'var(--space-3, 12px)',
              }}
            />
            <div
              style={{
                fontWeight: 600,
                fontSize: 'var(--font-size-md, 16px)',
              }}
            >
              {tx(it.title)}
            </div>
            {it.body && <div style={{ ...muted, marginTop: 6 }}>{tx(it.body)}</div>}
          </Card>
        ))}
      </div>
    </SectionShell>
  );
}
